import {
  LearningPath,
  LearningNode,
  LearningCategory,
  ValidationCriteria
} from '../types/learning-path';
import { ConceptCategory, Difficulty } from '../types/index';

const BASIC_VALIDATION: ValidationCriteria = {
  minimumScore: 70,
  requiredAttempts: 2,
  mustDemonstrate: ['correct_syntax', 'basic_logic']
};

const INTERMEDIATE_VALIDATION: ValidationCriteria = {
  minimumScore: 75,
  requiredAttempts: 3,
  mustDemonstrate: ['correct_syntax', 'proper_logic', 'clean_code']
};

const ADVANCED_VALIDATION: ValidationCriteria = {
  minimumScore: 75,
  requiredAttempts: 3,
  mustDemonstrate: ['correct_syntax', 'proper_logic', 'clean_code', 'godot_idioms']
};

// ============================================================================
// GODOT (GDSCRIPT) LEARNING NODES
// ============================================================================

const GODOT_FOUNDATIONS: LearningNode[] = [
  {
    id: 'godot_variables_output',
    title: 'Variables, Types & Affichage',
    description: 'Les bases de GDScript : stocker des données dans des variables et les afficher avec print()',
    category: LearningCategory.GODOT_BASICS,
    prerequisites: [],
    concepts: [ConceptCategory.VARIABLES, ConceptCategory.GODOT_PRINT_OUTPUT],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 20,
    validationCriteria: BASIC_VALIDATION,
    requiredSuccessfulExercises: 8
  },
  {
    id: 'godot_control_flow_functions',
    title: 'Conditions, Boucles & Fonctions',
    description: 'Faire prendre des décisions au code (if/elif/match), répéter des actions et organiser le code en fonctions (func)',
    category: LearningCategory.GODOT_CONTROL_FLOW,
    prerequisites: ['godot_variables_output'],
    concepts: [ConceptCategory.CONDITIONALS, ConceptCategory.LOOPS, ConceptCategory.METHODS],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 30,
    validationCriteria: BASIC_VALIDATION,
    requiredSuccessfulExercises: 10
  },
  {
    id: 'godot_data_structures',
    title: 'Structures de Données',
    description: 'Gérer des collections de données : Array et Dictionary pour inventaires, scores et ressources',
    category: LearningCategory.GODOT_DATA_STRUCTURES,
    prerequisites: ['godot_control_flow_functions'],
    concepts: [ConceptCategory.GODOT_ARRAYS_DICTS, ConceptCategory.LOOPS],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 35,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 10
  },
  {
    id: 'godot_nodes_lifecycle',
    title: 'Nodes, Scènes & Cycle de Vie',
    description: 'Le cœur de Godot : extends, _ready(), _process(delta), accéder aux nodes ($, get_node, @onready) et exposer des variables (@export)',
    category: LearningCategory.GODOT_NODES,
    prerequisites: ['godot_data_structures'],
    concepts: [ConceptCategory.GODOT_NODES_SCENES, ConceptCategory.GODOT_LIFECYCLE],
    difficulty: Difficulty.INTERMEDIATE,
    estimatedTime: 40,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 10
  },
  {
    id: 'godot_vector_math',
    title: 'Maths Vectorielles (Vector2)',
    description: 'Le langage du mouvement 2D : créer des Vector2, normaliser, mesurer des distances et interpoler pour des déplacements fluides',
    category: LearningCategory.GODOT_VECTORS,
    prerequisites: ['godot_control_flow_functions'],
    concepts: [ConceptCategory.GODOT_VECTOR_MATH],
    difficulty: Difficulty.INTERMEDIATE,
    estimatedTime: 35,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 10
  },
  {
    id: 'godot_signals_callbacks',
    title: 'Signaux & Callbacks',
    description: 'La communication entre nodes : déclarer, émettre et connecter des signaux, utiliser Callable et les fonctions lambda',
    category: LearningCategory.GODOT_SIGNALS_ASYNC,
    prerequisites: ['godot_nodes_lifecycle'],
    concepts: [ConceptCategory.GODOT_SIGNALS, ConceptCategory.GODOT_AWAIT_CALLABLES],
    difficulty: Difficulty.INTERMEDIATE,
    estimatedTime: 40,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 10
  },
  {
    id: 'godot_scene_instancing',
    title: 'Instancier des Scènes',
    description: 'Créer des objets par code : preload, instantiate(), add_child(), configurer les instances, connecter leurs signaux et les libérer',
    category: LearningCategory.GODOT_INSTANCING,
    prerequisites: ['godot_nodes_lifecycle'],
    concepts: [ConceptCategory.GODOT_SCENE_INSTANCING],
    difficulty: Difficulty.INTERMEDIATE,
    estimatedTime: 40,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 10
  },
  {
    id: 'godot_character_movement',
    title: 'Mouvement du Personnage',
    description: 'Déplacer un CharacterBody2D : velocity, move_and_slide(), top-down, gravité et saut, accélération et friction',
    category: LearningCategory.GODOT_MOVEMENT,
    prerequisites: ['godot_vector_math'],
    concepts: [ConceptCategory.GODOT_CHARACTER_MOVEMENT, ConceptCategory.GODOT_VECTOR_MATH],
    difficulty: Difficulty.INTERMEDIATE,
    estimatedTime: 40,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 10
  },
  {
    id: 'godot_async_advanced',
    title: 'Await & GDScript Avancé',
    description: 'Les mots-clés avancés : await sur signaux et timers, typage statique, class_name, enums et match',
    category: LearningCategory.GODOT_ADVANCED,
    prerequisites: ['godot_signals_callbacks'],
    concepts: [ConceptCategory.GODOT_AWAIT_CALLABLES, ConceptCategory.GODOT_TYPED_GDSCRIPT],
    difficulty: Difficulty.ADVANCED,
    estimatedTime: 45,
    validationCriteria: ADVANCED_VALIDATION,
    requiredSuccessfulExercises: 10
  },
  {
    id: 'godot_oop_patterns',
    title: 'POO & Patterns de Jeu',
    description: 'Structurer son code comme un pro : héritage et super, setters/getters, Resources custom, static, machines à états et Tween',
    category: LearningCategory.GODOT_OOP,
    prerequisites: ['godot_signals_callbacks', 'godot_async_advanced'],
    concepts: [ConceptCategory.GODOT_OOP_PATTERNS],
    difficulty: Difficulty.ADVANCED,
    estimatedTime: 50,
    validationCriteria: ADVANCED_VALIDATION,
    requiredSuccessfulExercises: 10
  }
];

// ============================================================================
// GODOT LEARNING PATH
// ============================================================================

export const GODOT_BEGINNER_PATH: LearningPath = {
  id: 'beginner_godot_programmer',
  name: 'Programmeur Godot Débutant',
  description: 'Parcours complet pour apprendre GDScript et Godot 4 : des variables jusqu\'aux vecteurs, l\'instanciation de scènes, le mouvement de personnage, les signaux, await et les patterns de jeu.',
  targetAudience: 'Débutants complets sur Godot, progression jusqu\'aux concepts avancés',
  estimatedDuration: '4-6 mois',
  nodes: GODOT_FOUNDATIONS,
  milestones: [
    {
      id: 'godot_basics_mastery',
      name: 'Les Bases Maîtrisées',
      description: 'Tu sais créer des variables GDScript, les afficher et faire des calculs simples',
      requiredNodes: ['godot_variables_output'],
      reward: 'Badge Variables & Affichage'
    },
    {
      id: 'godot_logic_mastery',
      name: 'Logique & Fonctions',
      description: 'Tu sais utiliser des conditions, des boucles et créer des fonctions en GDScript',
      requiredNodes: ['godot_variables_output', 'godot_control_flow_functions'],
      reward: 'Badge Logique de Code'
    },
    {
      id: 'godot_data_mastery',
      name: 'Structures de Données',
      description: 'Tu sais manipuler des Array et des Dictionary pour gérer les données de ton jeu',
      requiredNodes: ['godot_variables_output', 'godot_control_flow_functions', 'godot_data_structures'],
      reward: 'Badge Gestionnaire de Données'
    },
    {
      id: 'godot_nodes_mastery',
      name: 'Scripteur de Nodes',
      description: 'Tu sais scripter des nodes : _ready, _process, $, @onready et @export n\'ont plus de secret',
      requiredNodes: ['godot_variables_output', 'godot_control_flow_functions', 'godot_data_structures', 'godot_nodes_lifecycle'],
      reward: 'Badge Scripteur de Nodes'
    },
    {
      id: 'godot_vectors_mastery',
      name: 'Maître des Vecteurs',
      description: 'Tu sais manipuler des Vector2 : directions, distances, normalisation et interpolation',
      requiredNodes: ['godot_variables_output', 'godot_control_flow_functions', 'godot_vector_math'],
      reward: 'Badge Maître des Vecteurs'
    },
    {
      id: 'godot_signals_mastery',
      name: 'Maître des Signaux',
      description: 'Tu sais faire communiquer tes nodes avec des signaux, des Callable et des lambdas',
      requiredNodes: ['godot_variables_output', 'godot_control_flow_functions', 'godot_data_structures', 'godot_nodes_lifecycle', 'godot_signals_callbacks'],
      reward: 'Badge Maître des Signaux'
    },
    {
      id: 'godot_instancing_mastery',
      name: 'Invocateur de Scènes',
      description: 'Tu sais créer des objets par code : preload, instantiate, add_child et libérer proprement',
      requiredNodes: ['godot_variables_output', 'godot_control_flow_functions', 'godot_data_structures', 'godot_nodes_lifecycle', 'godot_scene_instancing'],
      reward: 'Badge Invocateur de Scènes'
    },
    {
      id: 'godot_movement_mastery',
      name: 'Personnage en Mouvement',
      description: 'Tu sais déplacer un CharacterBody2D : velocity, move_and_slide, gravité et saut',
      requiredNodes: ['godot_variables_output', 'godot_control_flow_functions', 'godot_vector_math', 'godot_character_movement'],
      reward: 'Badge Personnage en Mouvement'
    },
    {
      id: 'godot_advanced_mastery',
      name: 'Programmeur Godot Confirmé',
      description: 'Tu maîtrises await, le typage statique, class_name et les mots-clés avancés de GDScript',
      requiredNodes: ['godot_variables_output', 'godot_control_flow_functions', 'godot_data_structures', 'godot_nodes_lifecycle', 'godot_signals_callbacks', 'godot_async_advanced'],
      reward: 'Badge Programmeur Godot'
    },
    {
      id: 'godot_architect_mastery',
      name: 'Architecte du Code',
      description: 'Tu structures ton code comme un pro : héritage, set/get, Resources, state machines et Tween — le parcours complet est terminé',
      requiredNodes: ['godot_variables_output', 'godot_control_flow_functions', 'godot_data_structures', 'godot_nodes_lifecycle', 'godot_vector_math', 'godot_signals_callbacks', 'godot_scene_instancing', 'godot_character_movement', 'godot_async_advanced', 'godot_oop_patterns'],
      reward: 'Badge Architecte du Code'
    }
  ]
};

export const GODOT_ALL_PATHS = [GODOT_BEGINNER_PATH];

export const GODOT_ALL_NODES = GODOT_FOUNDATIONS;
